/**
 * Планировщик фоновой подгрузки: «через N секунд, если вкладка ещё видна».
 *
 * Вынесен отдельно от сети и DOM, чтобы логику задержки и видимости можно
 * было проверить тестом на подставных часах: настоящий `setTimeout` и
 * `document` сюда не зашиты, а приходят зависимостями.
 *
 * Правила:
 * - повторный `schedule` того же ключа ничего не делает — ни дубля запроса,
 *   ни сброса таймера (иначе частые ре-рендеры никогда бы не дали ему сработать);
 * - пока вкладка скрыта, задача ждёт и таймер не идёт; на возврате вкладки
 *   отсчёт начинается заново;
 * - `cancel` убирает и таймер, и намерение — экран урока больше не ждёт.
 */

export interface PrefetchSchedulerOptions {
  /** Сколько ждать «оседлости» на экране. */
  readonly debounceMs: number;
  readonly setTimeout: (callback: () => void, ms: number) => unknown;
  readonly clearTimeout: (handle: unknown) => void;
  readonly isVisible: () => boolean;
}

export interface PrefetchScheduler {
  /** Поставить задачу. `false` — такой ключ уже запланирован (дубль отброшен). */
  schedule: (key: string, run: () => void) => boolean;
  /** Отменить задачу по ключу. */
  cancel: (key: string) => void;
  /** Отменить всё (например, при размонтировании приложения). */
  cancelAll: () => void;
  /** Вкладка скрылась/вернулась: скрытая — таймеры стоят, вернувшаяся — перезапускаются. */
  setVisible: (visible: boolean) => void;
  /** Ключи, которые ждут своего часа (в том числе отложенные из-за скрытой вкладки). */
  pendingKeys: () => readonly string[];
}

export function createPrefetchScheduler(options: PrefetchSchedulerOptions): PrefetchScheduler {
  /** Задача, которую просили выполнить; живёт, пока её не отменили или не выполнили. */
  const wanted = new Map<string, () => void>();
  const timers = new Map<string, unknown>();
  let visible = options.isVisible();

  const arm = (key: string): void => {
    if (!visible || timers.has(key)) return;
    const run = wanted.get(key);
    if (run === undefined) return;
    const handle = options.setTimeout(() => {
      timers.delete(key);
      // Пока таймер шёл, задачу могли отменить или вкладку скрыть.
      const task = wanted.get(key);
      if (task === undefined || !visible) return;
      wanted.delete(key);
      task();
    }, options.debounceMs);
    timers.set(key, handle);
  };

  const stopTimer = (key: string): void => {
    const handle = timers.get(key);
    if (handle !== undefined) options.clearTimeout(handle);
    timers.delete(key);
  };

  return {
    schedule: (key, run) => {
      if (wanted.has(key) || timers.has(key)) return false;
      wanted.set(key, run);
      arm(key);
      return true;
    },

    cancel: (key) => {
      stopTimer(key);
      wanted.delete(key);
    },

    cancelAll: () => {
      for (const key of timers.keys()) stopTimer(key);
      timers.clear();
      wanted.clear();
    },

    setVisible: (next) => {
      if (next === visible) return;
      visible = next;
      if (!visible) {
        // Таймеры стоят, но намерение остаётся: вернёмся — начнём отсчёт заново.
        for (const key of timers.keys()) stopTimer(key);
        return;
      }
      for (const key of wanted.keys()) arm(key);
    },

    pendingKeys: () => [...wanted.keys()],
  };
}
